<template>
  <PTransparentLoader :loading="loading">
    <CRow v-if="serviceOrder">
      <CCol lg="12">
        <div class="d-flex align-items-center mb-3">
          <CButton
              color="link"
              class="p-0 mr-3"
              @click="goBack"
          >
            <CIcon name="cil-arrow-left" size="lg"/>
          </CButton>
          <h1 class="main-header mb-0">
            <translate translate-context="accounting.serviceOrder">Service Order</translate>
            #{{ serviceOrder.order_number }}
          </h1>
        </div>

        <!-- Service Order Info (merged with amounts and payment details) -->
        <CCard class="mb-3">
          <CCardHeader>
            <translate translate-context="accounting.serviceOrder">Service Order Information</translate>
          </CCardHeader>
          <CCardBody>
            <CRow>
              <CCol md="3">
                <div class="mb-3">
                  <small class="text-muted d-block">
                    <translate translate-context="accounting.serviceOrder">Order Number</translate>
                  </small>
                  <strong>{{ serviceOrder.order_number }}</strong>
                </div>
              </CCol>
              <CCol md="3">
                <div class="mb-3">
                  <small class="text-muted d-block">
                    <translate translate-context="accounting.serviceOrder">Status</translate>
                  </small>
                  <CBadge :color="getStatusColor(serviceOrder.status)">
                    {{ serviceOrder.status }}
                  </CBadge>
                </div>
              </CCol>
              <CCol md="3">
                <div class="mb-3">
                  <small class="text-muted d-block">
                    <translate translate-context="accounting.serviceOrder">Created At</translate>
                  </small>
                  {{ formatDate(serviceOrder.created_at) }}
                </div>
              </CCol>
              <CCol md="3">
                <div class="mb-3">
                  <small class="text-muted d-block">
                    <translate translate-context="accounting.serviceOrder">Paid At</translate>
                  </small>
                  {{ formatDate(serviceOrder.paid_at) }}
                </div>
              </CCol>
            </CRow>

            <CRow>
              <CCol md="3">
                <div class="mb-3">
                  <small class="text-muted d-block">
                    <translate translate-context="accounting.serviceOrder">Total Amount</translate>
                  </small>
                  <strong class="text-success">
                    {{ formatAmount(serviceOrder.total_amount) }} {{ serviceOrder.currency }}
                  </strong>
                </div>
              </CCol>
              <CCol md="3">
                <div class="mb-3">
                  <small class="text-muted d-block">
                    <translate translate-context="accounting.serviceOrder">Tax</translate>
                  </small>
                  {{ formatAmount(serviceOrder.amount_tax) }} {{ serviceOrder.currency }}
                </div>
              </CCol>
              <CCol md="3">
                <div class="mb-3">
                  <small class="text-muted d-block">
                    <translate translate-context="accounting.serviceOrder">Transaction Fee</translate>
                  </small>
                  {{ formatAmount(serviceOrder.payment_transaction_fee) }} {{ serviceOrder.currency }}
                </div>
              </CCol>
            </CRow>

            <CRow>
              <CCol md="3" v-if="serviceOrder.payer && serviceOrder.payer.email">
                <div class="mb-3">
                  <small class="text-muted d-block">
                    <translate translate-context="accounting.serviceOrder">Payment Email</translate>
                  </small>
                  {{ serviceOrder.payer.email }}
                </div>
              </CCol>
            </CRow>
          </CCardBody>
        </CCard>

        <!-- User -->
        <CCard class="mb-3" v-if="serviceOrder.user_id">
          <CCardHeader class="d-flex align-items-center justify-content-between">
            <span>
              <translate translate-context="accounting.serviceOrder">User</translate>
              #{{ serviceOrder.user_id }}
            </span>
            <router-link
              :to="{ name: 'customer_success.user', params: { id: serviceOrder.user_id }}"
              class="text-primary"
              title="View user in Customer Success"
            >
              <CIcon name="cil-external-link" size="sm"/>
            </router-link>
          </CCardHeader>
          <CCardBody>
            <UserInfo
              :user="serviceOrder.user_info"
              :user-id="serviceOrder.user_id"
              :current-source-id="serviceOrder.id"
              :scope="scope"
            />
          </CCardBody>
        </CCard>

        <!-- Service Order Items -->
        <CCard class="mb-3" v-if="serviceOrder.items && serviceOrder.items.length > 0">
          <CCardHeader>
            <translate translate-context="accounting.serviceOrder">Service Order Items</translate>
          </CCardHeader>
          <CCardBody class="p-0">
            <div
              v-for="item in serviceOrder.items"
              :key="item.id"
              class="service-item-card"
            >
              <div class="service-item-header">
                <div class="d-flex align-items-center">
                  <CBadge :color="getOrderTypeColor(item.order_type)" class="mr-2">
                    {{ item.order_type }}
                  </CBadge>
                  <span class="service-item-id text-muted">#{{ item.id }}</span>
                </div>
                <div class="service-item-amount">
                  <strong>{{ formatAmount(item.amount) }} {{ serviceOrder.currency }}</strong>
                </div>
              </div>

              <div class="service-item-body">
                <CRow>
                  <CCol md="6">
                    <div class="info-row">
                      <CIcon name="cil-camera" size="sm" class="text-muted mr-2"/>
                      <span class="info-label">
                        <translate translate-context="accounting.serviceOrder">Event:</translate>
                      </span>
                      <router-link
                        v-if="item.pica_service_id"
                        :to="{ name: 'picaServiceRedirect', params: { picaServiceId: item.pica_service_id }}"
                        class="info-link"
                      >
                        {{ item.pica_service_name || `PicaService #${item.pica_service_id}` }}
                      </router-link>
                      <span v-else class="text-muted">-</span>
                    </div>
                  </CCol>
                  <CCol md="6">
                    <div class="info-row">
                      <CIcon name="cil-briefcase" size="sm" class="text-muted mr-2"/>
                      <span class="info-label">
                        <translate translate-context="accounting.serviceOrder">Contract:</translate>
                      </span>
                      <span>{{ item.contract_name || '-' }}</span>
                    </div>
                  </CCol>
                </CRow>

                <!-- Base Plan Info -->
                <div v-if="item.base_plan" class="base-plan-box mt-3">
                  <div class="base-plan-header">
                    <CIcon name="cil-layers" size="sm" class="mr-2"/>
                    <strong>{{ item.base_plan.name }}</strong>
                    <CBadge v-if="item.base_plan.vertical" color="light" class="ml-2 text-dark">
                      {{ item.base_plan.vertical }}
                    </CBadge>
                  </div>
                  <div v-if="hasNonZeroSettings(item.base_plan)" class="base-plan-settings">
                    <span
                      v-for="(value, label) in item.base_plan.settings"
                      :key="label"
                      class="setting-tag"
                    >
                      {{ label }}: <strong>{{ value }}</strong>
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </CCardBody>
        </CCard>

        <!-- Ledger Entries -->
        <CCard>
          <CCardHeader>
            <translate translate-context="accounting.serviceOrder">Ledger Entries</translate>
          </CCardHeader>
          <CCardBody>
            <LedgerEntriesTable :entries="serviceOrder.ledger_entries"/>
          </CCardBody>
        </CCard>
      </CCol>
    </CRow>
  </PTransparentLoader>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import PTransparentLoader from '@/domain/core/components/PTransparentLoader.vue'
import LedgerEntriesTable from '../components/LedgerEntriesTable.vue'
import UserInfo from '../components/UserInfo.vue'
import { getRoutePrefix } from '../constants'

export default {
  name: 'ServiceOrderDetail',
  components: {
    PTransparentLoader,
    LedgerEntriesTable,
    UserInfo
  },
  data() {
    return {
      loading: true
    }
  },
  async mounted() {
    // Set scope first, then load data
    await this.setScope(this.scope)
    await this.loadServiceOrder()
  },
  computed: {
    ...mapGetters('accounting/serviceOrder', ['serviceOrder']),

    scopeType() {
      return this.$route.meta.scopeType || 'global'
    },

    scopeId() {
      if (this.scopeType === 'pica-service') {
        return this.$route.params.picaServiceId
      } else if (this.scopeType === 'contract-manager') {
        return this.$route.params.contractManagerId
      }
      return null
    },

    scope() {
      return {
        type: this.scopeType,
        id: this.scopeId ? parseInt(this.scopeId) : null
      }
    },

    listRouteName() {
      return getRoutePrefix(this.scopeType)
    }
  },
  watch: {
    '$route.params.id'() {
      this.loadServiceOrder()
    }
  },
  methods: {
    ...mapActions('accounting', ['setScope']),
    ...mapActions('accounting/serviceOrder', ['fetchServiceOrder']),

    goBack() {
      const params = {}
      if (this.scopeType === 'contract-manager' && this.scopeId) {
        params.contractManagerId = this.scopeId
      } else if (this.scopeType === 'pica-service' && this.scopeId) {
        params.picaServiceId = this.scopeId
      }
      this.$router.push({ name: this.listRouteName, params })
    },

    async loadServiceOrder() {
      this.loading = true
      try {
        await this.fetchServiceOrder(this.$route.params.id)
      } finally {
        this.loading = false
      }
    },

    getStatusColor(status) {
      const colors = {
        'paid': 'success',
        'pending': 'warning',
        'failed': 'danger'
      }
      return colors[status?.toLowerCase()] || 'secondary'
    },

    getOrderTypeColor(orderType) {
      const colors = {
        'subscription': 'primary',
        'addon': 'info',
        'upgrade': 'success',
        'renewal': 'warning'
      }
      return colors[orderType?.toLowerCase()] || 'secondary'
    },

    formatDate(dateString) {
      if (!dateString) return '-'
      return new Date(dateString).toLocaleString()
    },

    formatAmount(amount) {
      const num = parseFloat(amount) || 0
      return num.toFixed(2)
    },

    hasNonZeroSettings(basePlan) {
      return basePlan && basePlan.settings && Object.keys(basePlan.settings).length > 0
    }
  }
}
</script>

<style scoped>
.service-item-card {
  border-bottom: 1px solid #d8dbe0;
  padding: 1rem;
}

.service-item-card:last-child {
  border-bottom: none;
}

.service-item-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 0.75rem;
}

.service-item-id {
  font-size: 0.875rem;
}

.service-item-amount {
  font-size: 0.95rem;
}

.service-item-body {
  padding-left: 0.5rem;
}

.info-row {
  display: flex;
  align-items: center;
  margin-bottom: 0.25rem;
}

.info-label {
  font-size: 0.875rem;
  color: #768192;
  margin-right: 0.5rem;
}

.info-link {
  color: #321fdb;
  text-decoration: none;
}

.info-link:hover {
  text-decoration: underline;
}

.base-plan-box {
  background: #f8f9fa;
  border: 1px solid #e4e7ea;
  border-radius: 4px;
  padding: 0.75rem;
}

.base-plan-header {
  display: flex;
  align-items: center;
  margin-bottom: 0.5rem;
}

.base-plan-settings {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
}

.setting-tag {
  background: #fff;
  border: 1px solid #d8dbe0;
  border-radius: 3px;
  padding: 0.25rem 0.5rem;
  font-size: 0.8rem;
  color: #768192;
}

.setting-tag strong {
  color: #3c4b64;
}
</style>
